import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type {
  LogLevel,
  LogsQueryParams,
  LogsResponse,
  LogStats,
} from '@brickup/shared/dto'
import type { GlanceOptions } from './glance'
import { apiClient } from '@/api/client'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'

// --- API Functions ---

async function getLogs(params: LogsQueryParams): Promise<LogsResponse> {
  const queryParams: Record<string, string | number> = {}
  if (params.level) queryParams.level = params.level
  if (params.search) queryParams.search = params.search
  if (params.limit) queryParams.limit = params.limit
  if (params.before) queryParams.before = params.before

  const response = await apiClient.get<LogsResponse>('/logs', {
    params: queryParams,
  })
  return response.data
}

async function getLogStats(): Promise<LogStats> {
  const response = await apiClient.get<LogStats>('/logs/stats')
  return response.data
}

async function cleanLogs(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/logs/clean')
  return response.data
}

// --- Query Keys ---

export const LOGS_KEYS = {
  all: ['logs'] as const,
  entries: (level?: LogLevel, search?: string) =>
    ['logs', 'entries', level ?? 'all', search ?? ''] as const,
  stats: ['logs', 'stats'] as const,
}

// --- Hooks ---

const INFINITE_PAGE_SIZE = 50

/** Every five seconds on the Logs page; a surface that only glances at the
 *  counts passes a slower `refetchInterval`. Counting reads the whole file. */
export function useLogStats(options?: GlanceOptions) {
  return useQuery({
    queryKey: LOGS_KEYS.stats,
    queryFn: getLogStats,
    refetchInterval: 5000,
    ...options,
  })
}

export function useInfiniteLogs(
  level?: LogLevel,
  search?: string,
  autoRefresh = true,
) {
  return useInfiniteQuery({
    queryKey: [...LOGS_KEYS.entries(level, search), 'infinite'],
    queryFn: ({ pageParam }) =>
      getLogs({ level, search, limit: INFINITE_PAGE_SIZE, before: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: lastPage => {
      if (!lastPage.hasMore) return undefined
      return lastPage.oldestTimestamp ?? undefined
    },
    refetchInterval: autoRefresh ? 5000 : false,
  })
}

export function useCleanLogs() {
  return useMutationWithInvalidation(cleanLogs, [[...LOGS_KEYS.all]])
}
